/**
 * NoDataFound is used to display a fallback ui when there are no search results available.
 *
 * @module views/components/NoDataFound
 * @memberof -Common
 */
import './NoDataFound.scss';

import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import ActionGroups from '@ulta/core/components/ActionGroups/ActionGroups';
import Text from '@ulta/core/components/Text/Text';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import Divider from '@ulta/components/Divider/Divider';
import IconWithTitles from '@ulta/components/IconWithTitles/IconWithTitles';
/**
 * Represents a NoDataFound component
 *
 * @method
 * @param { NoDataFoundProps} props - React properties passed from composition
 * @returns NoDataFound
 */
export const NoDataFound = function( props ){
  const {
    icon,
    title,
    subTitle,
    pageTitle,
    variant,
    iconClass,
    resourceType,
    actions
  } = props;

  const { inflection } = useDeviceInflection();
  const isDesktop = inflection.DESKTOP;
  const hasDivider = resourceType === 'purchaseHistory' && isDesktop;
  const showMessageBlock = title || actions;
  return (
    <>
      <div
        className={ classNames( 'NoDataFound', {
          [`NoDataFound--${ variant }`]: variant,
          [`NoDataFound--${ resourceType }`]: resourceType
        } ) }
      >
        { pageTitle && (
          <div className='NoDataFound__Title'>
            <Text
              htmlTag='p'
              textStyle='title-6'
            >
              { pageTitle }
            </Text>
          </div>
        ) }
        { showMessageBlock && (
          <div className='NoDataFound__Message'>
            {
              title && (
                <IconWithTitles
                  icon={ icon }
                  headline={ title }
                  description={ subTitle }
                  iconClass={ iconClass }
                />
              )
            }

            { actions && (
              <div className='NoDataFound__action'>
                <ActionGroups { ...spreadProps( { ...actions } ) } />
              </div>
            ) }
          </div>
        ) }


      </div>
      { hasDivider && (
        <div className='NoDataFound__Divider'>
          <Divider type='gray' />
        </div>
      ) }
    </>
  );
};

/**
 * Property type definitions
 * @typedef NoDataFoundProps
 * @type {object}
 * @property {string} icon - set the icon
 * @property {string} title - set the title value
 * @property {string} subTitle - set the subTitle value
 * @property {string} pageTitle - set the pageTitle value
 * @property {string} resourceType - set the resourceType value
 * @property {object} actions - set the actions value
 */
export const propTypes = {
  icon: PropTypes.string,
  title: PropTypes.string,
  subTitle: PropTypes.string,
  pageTitle: PropTypes.string,
  variant: PropTypes.string,
  iconClass: PropTypes.string,
  resourceType: PropTypes.string,
  actions: PropTypes.object
};

/**
 * Default values for passed properties
 *
 * @type {object}
 * @property {string} variant='default' - Padding around the Icon.
*/
export const defaultProps = {
  variant: 'default'
};

NoDataFound.defaultProps = defaultProps;

NoDataFound.propTypes = propTypes;

export default NoDataFound;
